import { useEffect, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight, Share2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { detectVideoKind, youtubeEmbedUrl, facebookEmbedUrl } from "@/lib/media";

/**
 * Full-screen media lightbox with prev/next, keyboard support, share.
 * Handles image / MP4 / YouTube items.
 */
export default function Lightbox({ items, index, onClose, onIndexChange }) {
  const item = items?.[index];
  const videoRef = useRef();

  const goPrev = useCallback(() => {
    if (!items?.length) return;
    onIndexChange((index - 1 + items.length) % items.length);
  }, [index, items, onIndexChange]);

  const goNext = useCallback(() => {
    if (!items?.length) return;
    onIndexChange((index + 1) % items.length);
  }, [index, items, onIndexChange]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [goPrev, goNext, onClose]);

  const shareUrl = item?.slug ? `${window.location.origin}/project/${item.slug}` : "";

  const doShare = async () => {
    if (!shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: `LC Builders — ${item.label}`, url: shareUrl });
        return;
      } catch {
        /* cancelled */
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Unable to copy — link: " + shareUrl);
    }
  };

  if (!item) return null;

  const videoKind = detectVideoKind(item.video_url);
  const img = item.image_data || item.image_url;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      data-testid="lightbox"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between p-4 md:p-6 z-10 bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center gap-3 min-w-0">
          <div className="font-display uppercase tracking-widest text-xl md:text-2xl text-white truncate">
            {item.label}
          </div>
          {item._photo_count > 1 ? (
            <span
              data-testid="lightbox-photo-index"
              className="text-white/60 text-xs md:text-sm tracking-widest font-display uppercase hidden sm:inline"
            >
              Photo {item._photo_index + 1} / {item._photo_count}
            </span>
          ) : (
            <span className="text-white/40 text-sm tracking-widest hidden sm:inline">
              {index + 1} / {items.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={doShare}
            data-testid="lightbox-share"
            className="inline-flex items-center gap-2 border border-white/20 hover:border-[#84FB00] hover:text-[#4AB300] px-3 py-2 text-xs font-display tracking-widest uppercase transition-colors"
          >
            <Share2 size={14} /> Share
          </button>
          {item.slug && (
            <Link
              to={`/project/${item.slug}`}
              data-testid="lightbox-view-project"
              className="inline-flex items-center gap-2 bg-[#84FB00] hover:bg-[#6EE500] px-3 py-2 text-xs font-display tracking-widest uppercase text-white transition-colors"
            >
              <ExternalLink size={14} /> View Project
            </Link>
          )}
          <button
            onClick={onClose}
            data-testid="lightbox-close"
            className="p-2 border border-white/20 hover:border-white/50 text-white transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Prev/Next */}
      {items.length > 1 && (
        <>
          <button
            onClick={goPrev}
            data-testid="lightbox-prev"
            aria-label="Previous"
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 z-10 p-3 md:p-4 bg-black/40 hover:bg-[#84FB00] border border-white/20 hover:border-[#84FB00] transition-colors"
          >
            <ChevronLeft size={22} className="text-white" />
          </button>
          <button
            onClick={goNext}
            data-testid="lightbox-next"
            aria-label="Next"
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 z-10 p-3 md:p-4 bg-black/40 hover:bg-[#84FB00] border border-white/20 hover:border-[#84FB00] transition-colors"
          >
            <ChevronRight size={22} className="text-white" />
          </button>
        </>
      )}

      {/* Media */}
      <div className="max-w-[95vw] max-h-[85vh] flex items-center justify-center px-4">
        {videoKind === "mp4" ? (
          <video
            key={`${item.id}-${item._photo_index ?? 0}`}
            ref={videoRef}
            src={item.video_url}
            poster={img}
            data-testid="lightbox-video"
            controls
            autoPlay
            loop
            muted
            playsInline
            className="max-w-full max-h-[85vh]"
          />
        ) : videoKind === "youtube" ? (
          <iframe
            key={`${item.id}-${item._photo_index ?? 0}`}
            title={item.label}
            src={youtubeEmbedUrl(item.video_url, { controls: true })}
            data-testid="lightbox-iframe"
            allow="autoplay; encrypted-media; fullscreen"
            allowFullScreen
            className="w-[85vw] max-w-[1280px] aspect-video border-0"
          />
        ) : videoKind === "facebook" ? (
          <FacebookVideoEmbed
            key={`${item.id}-${item._photo_index ?? 0}`}
            url={item.video_url}
            label={item.label}
            poster={img}
          />
        ) : img ? (
          <img
            key={`${item.id}-${item._photo_index ?? 0}`}
            src={img}
            alt={item.alt || item.label}
            className="max-w-full max-h-[85vh] object-contain"
          />
        ) : (
          <div className="text-white/50">No media</div>
        )}
      </div>

      {item.alt && item.alt !== item.label && (
        <div className="absolute bottom-6 left-0 right-0 text-center text-white/60 text-sm px-6">
          {item.alt}
        </div>
      )}
    </div>
  );
}


/**
 * FacebookVideoEmbed — best-effort inline embed via Facebook's video plugin.
 * If the plugin renders (public videos), the iframe shows the video with
 * native FB controls. If Facebook refuses (private post, region block, or
 * broken reel), we surface a branded click-through fallback that opens the
 * reel in a new tab. There's no reliable programmatic way to detect the
 * failure since the iframe is cross-origin, so we always render both and let
 * the iframe overlay the fallback when it succeeds.
 */
function FacebookVideoEmbed({ url, label, poster }) {
  const src = facebookEmbedUrl(url, { width: 720 });
  return (
    <div className="relative w-[min(90vw,720px)] aspect-[2/3] max-h-[85vh] bg-[#0d0d0d] border border-white/10 overflow-hidden" data-testid="lightbox-facebook">
      {/* Fallback content (always rendered, sits under the iframe). */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
        {poster ? (
          <img src={poster} alt={label} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        ) : null}
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 bg-[#1877F2] px-3 py-1 mb-4 text-[10px] font-display uppercase tracking-widest text-white">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.99 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.99 22 12z"/>
            </svg>
            Facebook Video
          </div>
          <p className="text-white/85 max-w-xs mb-6">
            Some Facebook videos can't be embedded directly. Tap below to watch on Facebook.
          </p>
          <a
            href={url}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-2 bg-[#1877F2] hover:bg-[#0E63D4] px-5 py-3 font-display tracking-widest uppercase text-white text-sm transition-colors"
          >
            Watch on Facebook
          </a>
        </div>
      </div>

      {/* Facebook plugin iframe — overlays fallback when render succeeds. */}
      <iframe
        title={label || "Facebook video"}
        src={src}
        data-testid="lightbox-facebook-iframe"
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
        className="absolute inset-0 w-full h-full border-0"
      />
    </div>
  );
}
